import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { Address, MarketROI, Offering } from '@arrived/models';

import { PROPERTY_HASH_MAP } from '../Property.constants';
import { PropertyDetailsSection } from './PropertyDetailsSection';
import { ROISection } from './ROISection';

interface PropertyDetailsHistoricalReturnsProps {
  marketData: MarketROI | undefined;
  debtLeverage: number;
  offering: Offering;
  address: Address | undefined;
}

export const PropertyDetailsHistoricalReturns = ({
  marketData,
  debtLeverage,
  offering,
  address,
}: PropertyDetailsHistoricalReturnsProps) => {
  const isMarketROIEnabled = useMemo(
    () => Boolean(marketData && marketData.quarterlyDividends.length > 0) || offering.projectedAnnualDividendYield,
    [marketData, offering.projectedFirstDividendAmount]
  );

  if (!marketData || !isMarketROIEnabled) return null;

  const { zipCode } = address || ({} as Address);

  return (
    <PropertyDetailsSection
      hash={PROPERTY_HASH_MAP.calculator}
      title={
        <FormattedMessage id="property-details-calculator-title" defaultMessage="Historical Returns in the Market" />
      }
      description={
        <FormattedMessage
          id="property-details-calculator-description"
          defaultMessage={`Investing in Arrived rental homes can deliver returns to investors in two different ways:
                <ol>
                <li>Equity growth from property value appreciation</li>
                <li>Rental income</li>
                </ol>
                While we cannot predict future returns for our investors,
                below is some historic data around property value appreciation over 10 years &
                20 years and Arrived rental property dividends that may be helpful.
                <strong>It is important to note that past performance may not be indicative of future results.</strong>`}
          values={{
            strong: (parts: any) => <strong>{parts}</strong>,
            br: <br />,
            ol: (parts: any) => <ol>{parts}</ol>,
            li: (parts: any) => <li>{parts}</li>,
          }}
        />
      }
    >
      <ROISection marketData={marketData} zipCode={zipCode} debtLeverage={debtLeverage} offering={offering} />
    </PropertyDetailsSection>
  );
};
